// ─── src/pages/AboutPage.jsx ────────────────────────────────────────────
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InsightsIcon from '@mui/icons-material/Insights';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/* ───────────────────────── data ─────────────────────────────────────── */
const milestones = [
  { year: '2020', title: 'Founded',            desc: 'Lemda Intelligence was born to transform digital experiences.' },
  { year: '2021', title: '50+ Projects',       desc: 'Delivered 50+ cutting-edge web & mobile apps.' },
  { year: '2022', title: 'AI Platform Launch', desc: 'Released our in-house AI development toolkit.' },
  { year: '2023', title: 'Global Expansion',   desc: 'Opened offices in three new regions.' },
];
const stats = [
  { label: 'Projects',   end: 72, icon: <AutoAwesomeIcon fontSize="large" /> },
  { label: 'Clients',    end: 38, icon: <PublicIcon     fontSize="large" /> },
  { label: 'Team Size',  end: 25, icon: <InsightsIcon   fontSize="large" /> },
  { label: 'Awards',     end: 5,  icon: <RocketLaunchIcon fontSize="large" /> },
];
const values = [
  { title: 'Innovation', color: 'primary'   },
  { title: 'Integrity',  color: 'secondary' },
  { title: 'Excellence', color: 'success'   },
  { title: 'Empathy',    color: 'warning'   },
];

/* ───────────────────────── component ─────────────────────────────────── */
export default function AboutPage() {
  const theme = useTheme();
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  /* framer helper */
  const fade = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };

  return (
    <>
 

      {/* ───── overall wrapper ───── */}
      <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>

        {/* ───── hero (now truly centred) ───── */}
        <Box
          component={motion.section}
          initial="hidden"
          animate="visible"
          variants={fade}
          sx={{
            minHeight: { xs: '60vh', md: '70vh' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 2,
            background: `
              linear-gradient(rgba(0,0,0,.55), rgba(0,0,0,.7)),
              url("/hero-tech.jpg") center/cover no-repeat
            `,
            color: '#fff',
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant={isXs ? 'h3' : 'h2'}
              fontWeight={800}
              sx={{
                lineHeight: 1.15,
                background: 'linear-gradient(90deg,#ff6ec4 0%,#7873f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              We Are Lemda&nbsp;Intelligence
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, opacity: 0.9 }}>
              AI-driven web&nbsp;&&nbsp;mobile solutions that elevate brands worldwide.
            </Typography>
          </Container>
        </Box>

        {/* ───── stats section ───── */}
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={4} justifyContent="center">
            {stats.map(({ label, end, icon }, i) => (
              <Grid key={i} item xs={6} sm={4} md={3}>
                <motion.div
                  variants={fade}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    {icon}
                    <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>
                      {inView && <CountUp end={end} duration={2} />}+
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      {label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ───── vision & mission ───── */}
        <Container sx={{ py: { xs: 4, md: 8 } }}>
          <Grid container spacing={4}>
            {[
              {
                title: 'Our Vision',
                text: 'To be the world’s most trusted partner for intelligent digital experiences—where innovation meets impact.',
              },
              {
                title: 'Our Mission',
                text: 'Empower businesses with AI-powered platforms that are scalable, secure, and stunningly designed.',
              },
            ].map((item, i) => (
              <Grid key={i} item xs={12} md={6}>
                <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      borderRadius: 3,
                      backdropFilter: 'blur(6px)',
                      bgcolor: theme.palette.mode === 'light'
                        ? 'rgba(255,255,255,0.85)'
                        : 'rgba(32,32,32,0.85)',
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {item.text}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ───── core values chips ───── */}
        <Container sx={{ pb: { xs: 6, md: 10 } }}>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            justifyContent="center"
          >
            {values.map(({ title, color }) => (
              <Chip
                key={title}
                label={title}
                color={color}
                sx={{ fontSize: '1rem', px: 3, py: 2, m: 1 }}
                component={motion.div}
                whileHover={{ scale: 1.08 }}
              />
            ))}
          </Stack>
        </Container>

        {/* ───── journey timeline ───── */}
        <Box ref={ref} sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="md">
            <Typography
              variant="h4"
              align="center"
              fontWeight={700}
              gutterBottom
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fade}
            >
              Our Journey
            </Typography>

            <Timeline position="alternate">
              {milestones.map((m, i) => (
                <TimelineItem key={i}>
                  <TimelineSeparator>
                    <TimelineDot color="primary" />
                    {i < milestones.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Card elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {m.year} — {m.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {m.desc}
                      </Typography>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Container>
        </Box>
      </Box>


    </>
  );
}
